import { useRef, useState } from 'react'
import { site } from '@/content/site'
import { useReducedMotion } from '@/hooks/useReducedMotion'
import { playgroundSection } from '@/lib/flags'
import { EASE, gsap, useGSAP } from '@/lib/gsap'
import { damp } from '@/lib/math'
import { useAppStore } from '@/lib/store'
import { hasWebGL2 } from '@/webgl/capabilities'
import { VoiceRipple } from '../Ripple'
import styles from './Preloader.module.css'
import { formatCounter, isPreloadComplete, PRELOAD_TIMING, preloadTarget } from './progress'

/** The wordmark, one span per letter. Decorative: the whole screen is `aria-hidden`. */
const LETTERS = Array.from(site.name)

/** Seconds during which the preloader looks for a hero `<SceneCanvas>` to wait for. */
const HERO_DISCOVERY_SECONDS = 1.5

/** Fonts the first screens need. `document.fonts.ready` alone can resolve before they start. */
const FONT_PROBES = [
  '600 1em Technor',
  '400 1em "Geist Mono Variable"',
  '500 1em Switzer',
  'italic 500 1em Boska',
]

function loadFonts(): Promise<void> {
  if (typeof document === 'undefined' || !('fonts' in document)) return Promise.resolve()
  return Promise.all(FONT_PROBES.map((font) => document.fonts.load(font).catch(() => []))).then(
    () => undefined,
    () => undefined,
  )
}

/**
 * Covers the page during `boot`: a pearl screen with the wordmark breathing in, a prism
 * voice ripple and a tabular counter. It waits for the fonts, the hero scene (when there is
 * one, capped at 6s) and a short minimum, blending real and simulated progress so the
 * counter never stalls or jumps.
 *
 * Exit: the ripple swells and the page opens from its centre through a soft, iridescent
 * wavefront. `phase` becomes `reveal` as the exit starts (the hero and nav intros overlap
 * it) and `ready` when it ends, when this unmounts. Reduced motion: a short fade.
 * It never renders in playground mode, and it is `aria-hidden` so it can't trap a screen
 * reader; it is removed from the DOM once the page is ready.
 */
export function Preloader() {
  const notReady = useAppStore((state) => state.phase !== 'ready')
  const [eligible] = useState(
    () => playgroundSection === null && useAppStore.getState().phase === 'boot',
  )
  if (!eligible || !notReady) return null
  return <PreloaderScreen />
}

function PreloaderScreen() {
  const rootRef = useRef<HTMLDivElement>(null)
  const reducedMotion = useReducedMotion()

  useGSAP(
    (_context, contextSafe) => {
      const root = rootRef.current
      if (!root || !contextSafe) return
      const store = useAppStore.getState()
      const reduced = store.reducedMotion
      const timing = reduced ? PRELOAD_TIMING.reduced : PRELOAD_TIMING.full
      const q = gsap.utils.selector(root)
      const letters = q('[data-letter]')
      const fades = q('[data-fade]')
      const ripple = q('[data-ripple]')[0] as HTMLElement | undefined
      const zeros = q('[data-zeros]')[0] as HTMLElement | undefined
      const digits = q('[data-digits]')[0] as HTMLElement | undefined
      const bar = q('[data-bar]')[0] as HTMLElement | undefined

      // Arriving mid-page on reload would play the intro over the wrong section.
      if (!window.location.hash) {
        window.history.scrollRestoration = 'manual'
        window.scrollTo(0, 0)
      }

      let fontsReady = false
      let heroReady = Boolean(store.readyScenes.hero)
      let heroRequired = false
      const webgl = hasWebGL2()
      let cancelled = false
      void loadFonts().then(() => {
        if (!cancelled) fontsReady = true
      })
      const unsubscribe = useAppStore.subscribe((state) => {
        if (state.readyScenes.hero) heroReady = true
      })

      if (!reduced) {
        gsap.fromTo(
          letters,
          { yPercent: 118, fontWeight: 280 },
          {
            yPercent: 0,
            fontWeight: 600,
            duration: 1.8,
            stagger: { each: 0.065, from: 'center' },
            ease: EASE.out,
            delay: 0.1,
          },
        )
        gsap.from(fades, {
          autoAlpha: 0,
          y: 12,
          duration: 1.4,
          stagger: 0.12,
          ease: EASE.out,
          delay: 0.5,
        })
        if (ripple) {
          gsap.from(ripple, { autoAlpha: 0, scale: 0.72, duration: 2.6, ease: EASE.out })
        }
      }

      let shown = 0
      let lastCounter = ''
      const render = () => {
        const { zeros: z, digits: d } = formatCounter(shown)
        const text = z + d
        if (text !== lastCounter) {
          lastCounter = text
          if (zeros) zeros.textContent = z
          if (digits) digits.textContent = d
        }
        if (bar) bar.style.transform = `scaleX(${shown.toFixed(4)})`
      }

      const exit = contextSafe(() => {
        const setPhase = useAppStore.getState().setPhase
        root.dataset.state = 'exit'

        if (reduced) {
          gsap
            .timeline({ onComplete: () => setPhase('ready') })
            .call(() => setPhase('reveal'), undefined, 0)
            .to(root, { autoAlpha: 0, duration: 0.35, ease: 'none' }, 0.05)
          return
        }

        // The page opens from the ripple's centre, the voice's source.
        const box = ripple?.getBoundingClientRect()
        const width = window.innerWidth
        const height = window.innerHeight
        const ox = box ? box.left + box.width / 2 : width / 2
        const oy = box ? box.top + box.height / 2 : height / 2
        const feather = Math.max(width, height) * 0.12
        const reach =
          Math.hypot(Math.max(ox, width - ox), Math.max(oy, height - oy)) + feather * 1.6
        root.style.setProperty('--ox', `${ox.toFixed(1)}px`)
        root.style.setProperty('--oy', `${oy.toFixed(1)}px`)
        root.style.setProperty('--feather', `${feather.toFixed(1)}px`)
        if (useAppStore.getState().quality === 'low') root.dataset.rim = 'off'
        const hole = { r: -feather * 1.6 }
        const setHole = () => {
          root.style.setProperty('--hole', `${hole.r.toFixed(1)}px`)
          root.style.setProperty('--rim-turn', `${((hole.r / reach) * 120).toFixed(2)}deg`)
        }
        setHole()

        gsap
          .timeline({ onComplete: () => setPhase('ready') })
          .call(() => setPhase('reveal'), undefined, 0)
          .to(fades, { autoAlpha: 0, y: -10, duration: 0.7, stagger: 0.05, ease: EASE.inOut }, 0)
          .to(
            letters,
            {
              yPercent: -118,
              fontWeight: 200,
              duration: 1,
              stagger: { each: 0.05, from: 'center' },
              ease: EASE.in,
            },
            0.05,
          )
          .to(ripple ?? [], { scale: 2.5, autoAlpha: 0, duration: 1.9, ease: 'power2.out' }, 0.12)
          .to(hole, { r: reach, duration: 1.7, ease: EASE.inOut, onUpdate: setHole }, 0.3)
      })

      const start = performance.now()
      let leaving = false
      const tick = (_time: number, deltaMs: number) => {
        const dt = Math.min(Math.max(deltaMs, 1), 100) / 1000
        const elapsed = (performance.now() - start) / 1000
        if (!heroRequired && webgl && elapsed < HERO_DISCOVERY_SECONDS) {
          heroRequired = document.querySelector('[data-scene="hero"]') !== null
        }
        const state = { elapsed, ...timing, fontsReady, heroReady, heroRequired }
        const complete = isPreloadComplete(state)
        // A small overshoot lets the damped value actually arrive at 100.
        const target = preloadTarget(state) + (complete ? 0.03 : 0)
        shown = Math.min(complete ? 1 : 0.99, Math.max(shown, damp(shown, target, 3.8, dt)))
        render()
        if (complete && shown >= 1 && !leaving) {
          leaving = true
          gsap.ticker.remove(tick)
          // A held breath at 100 before the page opens.
          gsap.delayedCall(reduced ? 0.05 : 0.28, exit)
        }
      }
      render()
      gsap.ticker.add(tick)

      return () => {
        cancelled = true
        gsap.ticker.remove(tick)
        unsubscribe()
      }
    },
    { scope: rootRef },
  )

  return (
    <div ref={rootRef} className={styles.root} data-state="boot" aria-hidden="true">
      <div className={styles.wash} />
      <div className={styles.rim} />
      <div className={styles.stage}>
        <div className={styles.ripple} data-ripple>
          <VoiceRipple rings={5} period={6.4} playing={!reducedMotion} />
        </div>
        <p className={styles.wordmark}>
          <span className={styles.mask}>
            {LETTERS.map((letter, index) => (
              <span key={index} className={styles.letter} data-letter>
                {letter}
              </span>
            ))}
          </span>
        </p>
      </div>
      <div className={styles.meta}>
        <span className={styles.motto} data-fade>
          {site.motto}
        </span>
        <span className={styles.counter} data-fade>
          <span className={styles.zeros} data-zeros>
            000
          </span>
          <span className={styles.digits} data-digits />
        </span>
      </div>
      <div className={styles.track} data-fade>
        <span className={styles.bar} data-bar />
      </div>
    </div>
  )
}
